import React from 'react';
import PropTypes from 'prop-types';
import { graphql, createRefetchContainer } from 'react-relay';

// components
import { PdpSharedShippingInfo } from '../PdpSharedShippingInfo/PdpSharedShippingInfo';
import {
    methodNote,
    costNote,
    requestQuoteNotice,
    methodNoteFreeShippingTestVariant,
} from '../messages/pdpDetailsMessages';
import { PdpSharedShippingInfoLink } from '../PdpSharedShippingInfo/PdpSharedShippingInfoLink';

// styles
import styles from './PdpSharedDetail.scss';

// helpers
import get from 'lodash.get';
import { getIsFreeShipping } from 'dibs-search-product-tile/exports/freeShippingHelpers';
import { trackPdpFreeShippingDisplay } from '../helpers/pdpTestVariantsHelpers';

export class PdpSharedDetailsShippingComponent extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            modalOpen: false,
            hasLoggedIn: false,
            address: null,
            hasFiredFreeShippingTracking: false,
        };
    }

    componentDidMount() {
        this.fireFreeShippingTracking();
    }

    componentDidUpdate() {
        this.fireFreeShippingTracking();
    }

    fireFreeShippingTracking() {
        const { hasFiredFreeShippingTracking } = this.state;
        const { itemId, item, viewer } = this.props;

        const userRegions = viewer?.regionalInfo?.[0]?.regionsByZipCode || [];
        const isFreeShipping = getIsFreeShipping({ item, userRegions });

        // GA tracking for displaying free shipping
        if (!hasFiredFreeShippingTracking && isFreeShipping && itemId) {
            trackPdpFreeShippingDisplay(itemId);
            this.setState({
                hasFiredFreeShippingTracking: true,
            });
        }
    }

    render() {
        const {
            item,
            itemId,
            viewer,
            currency,
            user,
            userId,
            shipmentQuote,
            shipment,
            relay,
            isPdpFreeShippingTestVariant,
        } = this.props;

        const leadTime = get(item, 'attributes.leadTime.0.option.urlLabel');
        const quoteRequestStatus = get(viewer, 'relevantShipping.quoteRequestStatus');
        const showQuoteDetails = quoteRequestStatus !== 'requestQuote';

        return (
            <span className={styles.shippingInfoContainer} data-tn="pdp-spec-shipping">
                <PdpSharedShippingInfo
                    currency={currency}
                    shipmentQuote={shipmentQuote}
                    shipment={shipment}
                    leadTime={leadTime}
                    quoteRequestStatus={quoteRequestStatus}
                    renderTooltip
                >
                    {isPdpFreeShippingTestVariant ? (
                        <div className={styles.quoteContainer}>
                            {showQuoteDetails && (
                                <React.Fragment>
                                    {methodNoteFreeShippingTestVariant}{' '}
                                </React.Fragment>
                            )}
                            <PdpSharedShippingInfoLink
                                currency={currency}
                                shipmentQuote={shipmentQuote}
                                userId={userId}
                                item={item}
                                itemId={itemId}
                                user={user}
                                viewer={viewer}
                                showQuoteDetails={showQuoteDetails}
                                quoteRequestStatus={quoteRequestStatus}
                                onSuccessfulQuoteRequest={relay.refetch}
                                displayInline={showQuoteDetails}
                            />
                        </div>
                    ) : (
                        <React.Fragment>
                            <PdpSharedShippingInfoLink
                                currency={currency}
                                shipmentQuote={shipmentQuote}
                                userId={userId}
                                item={item}
                                itemId={itemId}
                                user={user}
                                viewer={viewer}
                                showQuoteDetails={showQuoteDetails}
                                quoteRequestStatus={quoteRequestStatus}
                                onSuccessfulQuoteRequest={relay.refetch}
                            />
                            {!showQuoteDetails && (
                                <p className={styles.requestQuoteNotice}>{requestQuoteNotice}</p>
                            )}
                            {showQuoteDetails && (
                                <div className={styles.notesContainer}>
                                    <div className={styles.note}>{methodNote}</div>
                                    <div className={styles.note}>{costNote}</div>
                                </div>
                            )}
                        </React.Fragment>
                    )}
                </PdpSharedShippingInfo>
            </span>
        );
    }
}

PdpSharedDetailsShippingComponent.propTypes = {
    currency: PropTypes.string,
    userId: PropTypes.string,
    user: PropTypes.object,
    itemId: PropTypes.string,
    viewer: PropTypes.object.isRequired,
    item: PropTypes.object,
    shipmentQuote: PropTypes.object,
    shipment: PropTypes.object,
    relay: PropTypes.object.isRequired,
    isPdpFreeShippingTestVariant: PropTypes.bool,
};

export const PdpSharedDetailShipping = createRefetchContainer(
    PdpSharedDetailsShippingComponent,
    {
        viewer: graphql`
            fragment PdpSharedDetailShipping_viewer on Viewer {
                ...PdpSharedShippingInfoLink_viewer
                relevantShipping(itemId: $itemId, buyerId: $userId) {
                    quoteRequestStatus
                }
                regionalInfo(zipCode: $userZipCode, countryCode: $userCountryCode)
                    @include(if: $fetchRegionalInfo) {
                    regionsByZipCode {
                        displayName
                    }
                }
            }
        `,
        user: graphql`
            fragment PdpSharedDetailShipping_user on User {
                ...PdpSharedShippingInfoLink_user
            }
        `,
        item: graphql`
            fragment PdpSharedDetailShipping_item on Item {
                ...PdpSharedShippingInfoLink_item
                hasShipmentQuotes
                hasPreQuotedShipping
                itemIsOnHold: isOnHold(buyerId: $userId)
                attributes {
                    leadTime {
                        option {
                            urlLabel
                        }
                    }
                }
                ...freeShippingHelpers_item
            }
        `,
        shipmentQuote: graphql`
            fragment PdpSharedDetailShipping_shipmentQuote on ShipmentQuote {
                isCustom
                isNew
                status
                ...PdpSharedShippingInfoLink_shipmentQuote
                ...PdpSharedShippingInfo_shipmentQuote
            }
        `,
        shipment: graphql`
            fragment PdpSharedDetailShipping_shipment on Shipment {
                ...PdpSharedShippingInfo_shipment
            }
        `,
    },
    graphql`
        query PdpSharedDetailShippingRefetchQuery(
            $userId: String!
            $fetchUser: Boolean!
            $itemId: String!
            $userZipCode: String = "0"
            $fetchRegionalInfo: Boolean = false
            $userCountryCode: String = ""
        ) {
            viewer {
                ...PdpSharedDetailShipping_viewer
                relevantShipping(itemId: $itemId, buyerId: $userId) {
                    shipmentQuote {
                        ...PdpSharedDetailShipping_shipmentQuote
                    }
                    shipment {
                        ...PdpSharedDetailShipping_shipment
                    }
                }
            }
        }
    `
);
